import type { VoiceMemo } from '@/lib/api';
import type { VoiceMemoGroup } from '@/components/features/voice-memo-session-group';

/**
 * Extract timestamp from voice memo filename
 * Pattern: YYYYMMDD HHMMSS-*.m4a or YYYYMMDD HHMMSS.m4a
 */
function extractTimestamp(memo: VoiceMemo): string | null {
  // Try to extract from filename pattern: YYYYMMDD HHMMSS
  const match = memo.filename.match(/(\d{8})\s+(\d{6})/);
  if (match) {
    return match[2]; // Return HHMMSS
  }
  
  // Fallback: try session data if available
  if (memo.session?.date) {
    // Session date might have time info, but we'll use date for grouping
    return null;
  }
  
  return null;
}

/**
 * Group voice memos by time proximity (within 1 hour)
 * Memos recorded within 1 hour of each other are grouped into sessions
 */
export function groupMemosBySession(memos: VoiceMemo[]): (VoiceMemo | VoiceMemoGroup)[] {
  const ONE_HOUR_SECONDS = 3600;
  const grouped: Map<string, VoiceMemo[]> = new Map();
  const ungrouped: VoiceMemo[] = [];
  
  // Group memos by date first
  const byDate = new Map<string, VoiceMemo[]>();
  memos.forEach(memo => {
    if (!byDate.has(memo.date)) {
      byDate.set(memo.date, []);
    }
    byDate.get(memo.date)!.push(memo);
  });
  
  // Within each date, group by time proximity
  byDate.forEach((dateMemos, date) => {
    // Sort by timestamp (if available) or by filename
    const sorted = dateMemos.sort((a, b) => {
      const timeA = extractTimestamp(a) || '000000';
      const timeB = extractTimestamp(b) || '000000';
      return timeA.localeCompare(timeB);
    });
    
    let currentSession: VoiceMemo[] = [];
    let sessionStartTime: string | null = null;
    
    sorted.forEach((memo) => {
      const memoTime = extractTimestamp(memo) || '000000';
      
      if (!sessionStartTime) {
        // Start new session
        sessionStartTime = memoTime;
        currentSession = [memo];
      } else {
        // Check if within 1 hour
        const startTime = parseInt(sessionStartTime.substring(0, 2)) * 3600 + 
                         parseInt(sessionStartTime.substring(2, 4)) * 60 + 
                         parseInt(sessionStartTime.substring(4, 6) || '0');
        const currentTime = parseInt(memoTime.substring(0, 2)) * 3600 + 
                           parseInt(memoTime.substring(2, 4)) * 60 + 
                           parseInt(memoTime.substring(4, 6) || '0');
        const diffSeconds = currentTime - startTime;
        
        if (diffSeconds <= ONE_HOUR_SECONDS && diffSeconds >= 0) {
          // Within 1 hour, add to current session
          currentSession.push(memo);
        } else {
          // Save current session and start new one
          if (currentSession.length > 1) {
            const sessionId = `session-${date}-${sessionStartTime}`;
            grouped.set(sessionId, currentSession);
          } else {
            ungrouped.push(...currentSession);
          }
          sessionStartTime = memoTime;
          currentSession = [memo];
        }
      }
    });
    
    // Save final session
    if (currentSession.length > 1) {
      const sessionId = `session-${date}-${sessionStartTime}`;
      grouped.set(sessionId, currentSession);
    } else if (currentSession.length === 1) {
      ungrouped.push(...currentSession);
    }
  });
  
  // Convert groups to array format
  const result: (VoiceMemo | VoiceMemoGroup)[] = [];
  
  // Add grouped sessions
  grouped.forEach((memos, sessionId) => {
    const firstMemo = memos[0];
    const lastMemo = memos[memos.length - 1];
    const group: VoiceMemoGroup = {
      id: sessionId,
      date: firstMemo.date,
      startTime: extractTimestamp(firstMemo) || '000000',
      endTime: extractTimestamp(lastMemo) || extractTimestamp(firstMemo) || '000000',
      memos
    };
    result.push(group);
  });
  
  // Add ungrouped memos
  result.push(...ungrouped);
  
  // Sort by date (most recent first), then by time
  result.sort((a, b) => {
    const dateA = 'date' in a ? a.date : (a as VoiceMemo).date;
    const dateB = 'date' in b ? b.date : (b as VoiceMemo).date;
    if (dateA !== dateB) {
      return dateB.localeCompare(dateA);
    }
    const timeA = 'startTime' in a ? a.startTime : extractTimestamp(a as VoiceMemo) || '000000';
    const timeB = 'startTime' in b ? b.startTime : extractTimestamp(b as VoiceMemo) || '000000';
    return timeB.localeCompare(timeA);
  });
  
  return result;
}

